import { useState } from 'react';
import { useMutation, useQuery } from '@tanstack/react-query';
import { api } from '@/lib/api';
import ComposerApply, { ComposerHistory } from './ComposerApply';
import {
  Badge,
  Button,
  Card,
  ErrorState,
  Input,
  PageHeader,
  Select,
} from '@/components/ui';

/**
 * Describe a bot and get a filled-in configuration; or point it at a bot that
 * is already running and let it read its own worst calls.
 *
 * Configuring a bot means seven tabs. Every field has a sensible default and
 * most should be left alone — but somebody setting up their first bot has no
 * way to know which, so they either accept everything and get a generic
 * screener or change things they do not understand.
 *
 * Nothing here saves anything. Both tools return a proposal, and the operator
 * applies it through the ordinary form, which validates it the same way it
 * validates anything typed by hand.
 */

interface BuildResult {
  config: Record<string, unknown>;
  notes: string[];
  model: string;
  reminder: string;
}

interface Finding {
  problem: string;
  evidence?: string;
  change?: string;
  field?: string;
  severity?: string;
}

interface DiagnoseResult {
  bot?: { id: string; name: string };
  calls_read: number;
  findings: Finding[];
  proposal: Record<string, unknown>;
  notes: string[];
  reminder?: string;
}

const SEVERITY: Record<string, 'red' | 'amber' | 'gray'> = {
  high: 'red',
  medium: 'amber',
  low: 'gray',
};

function Proposal({ config }: { config: Record<string, unknown> }) {
  if (!Object.keys(config).length) return null;
  return (
    <div className="mt-3">
      <div className="mb-1 text-xs uppercase tracking-wide text-ink-500">
        Proposed configuration
      </div>
      {/* Shown as JSON on purpose. It is exactly what the bot form posts, so
          an operator can read it, edit it and paste it — rather than a
          prettified summary that hides a field they would have changed. */}
      <pre className="max-h-96 overflow-auto rounded-md border border-line bg-sunken p-3 text-2xs leading-relaxed text-ink-700 dark:text-ink-200">
        {JSON.stringify(config, null, 2)}
      </pre>
      <Button
        variant="secondary"
        size="sm"
        className="mt-2"
        onClick={() => {
          navigator.clipboard?.writeText(JSON.stringify(config, null, 2));
        }}
      >
        Copy
      </Button>
    </div>
  );
}

function Notes({ notes }: { notes: string[] }) {
  if (!notes.length) return null;
  return (
    <ul className="mt-3 space-y-1">
      {notes.map((note) => (
        <li
          key={note}
          className="text-xs leading-relaxed text-ink-600 dark:text-ink-300"
        >
          · {note}
        </li>
      ))}
    </ul>
  );
}

export default function Composer() {
  const [tab, setTab] = useState<'build' | 'diagnose'>('build');
  const [description, setDescription] = useState('');
  const [companyName, setCompanyName] = useState('');
  const [botId, setBotId] = useState('');
  // A fault is almost never in one bot -- it is in the engine all of them
  // were built from. Diagnosing the engine reads every bot's calls at once.
  const [scope, setScope] = useState<'bot' | 'engine'>('bot');
  const [engineId, setEngineId] = useState('');
  const [calls, setCalls] = useState(12);

  const bots = useQuery({
    queryKey: ['composer-bots'],
    queryFn: () =>
      api.get<{
        data: Array<{
          id: string;
          name: string;
          code: string;
          client_name: string;
          readable_calls: number;
        }>;
      }>('/admin/composer/bots'),
  });

  const engines = useQuery({
    queryKey: ['composer-engines'],
    queryFn: () =>
      api.get<{
        data: Array<{
          id: string;
          name: string;
          bots: number;
          readable_calls: number;
        }>;
      }>('/admin/composer/engines'),
  });

  const build = useMutation({
    mutationFn: () =>
      api.post<BuildResult>('/admin/composer/build', {
        description,
        ...(companyName ? { company_name: companyName } : {}),
      }),
  });

  const diagnose = useMutation({
    mutationFn: () =>
      api.post<DiagnoseResult>('/admin/composer/diagnose', {
        ...(scope === 'engine' ? { engine_id: engineId } : { bot_id: botId }),
        calls,
      }),
  });

  return (
    <div className="mx-auto max-w-4xl">
      <PageHeader
        title="Composer"
        description="Describe a bot and get a configuration, or let it read a bot's own calls and say what to change."
      />

      <div className="mb-4 flex gap-2">
        <Button
          variant={tab === 'build' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setTab('build')}
        >
          Build from a description
        </Button>
        <Button
          variant={tab === 'diagnose' ? 'primary' : 'secondary'}
          size="sm"
          onClick={() => setTab('diagnose')}
        >
          Fix an existing bot
        </Button>
      </div>

      {tab === 'build' ? (
        <Card
          title="Describe the bot"
          description="What the business does, who it calls, and what the bot should establish before a human picks up."
        >
          <label className="block text-xs text-ink-600 dark:text-ink-300">
            Company name (optional)
            <Input
              value={companyName}
              onChange={(e) => setCompanyName(e.target.value)}
              placeholder="Home Solve Pro"
              className="mt-1"
            />
          </label>

          <label className="mt-3 block text-xs text-ink-600 dark:text-ink-300">
            Description
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={8}
              placeholder={
                'We are a home services company in Florida. We dial homeowner ' +
                'lists and want to confirm the person owns their home and what ' +
                'they need doing, then hand them straight to a closer. Keep it ' +
                'short — under a minute.'
              }
              className="mt-1 w-full rounded-md border border-line bg-surface px-3 py-2 text-sm text-ink-800 placeholder:text-ink-400 focus:outline-none focus:ring-2 focus:ring-brand-500/40 dark:text-ink-100"
            />
          </label>

          <p className="mt-2 text-xs text-ink-500">
            The transfer destination and the compliance settings are never set
            here. Those are facts about your dialer and positions you take, not
            something to be guessed at.
          </p>

          {build.error ? (
            <div className="mt-3">
              <ErrorState error={build.error} retry={() => build.mutate()} />
            </div>
          ) : null}

          <Button
            className="mt-3"
            onClick={() => build.mutate()}
            disabled={build.isPending || description.trim().length < 20}
          >
            {build.isPending ? 'Drafting…' : 'Draft a configuration'}
          </Button>

          {build.data ? (
            <div className="mt-4 border-t border-line pt-3">
              <div className="flex items-center gap-2">
                <Badge tone="green">draft</Badge>
                <span className="text-xs text-ink-500">{build.data.model}</span>
              </div>
              <p className="mt-2 text-xs text-ink-600 dark:text-ink-300">
                {build.data.reminder}
              </p>
              <Notes notes={build.data.notes} />
              <Proposal config={build.data.config} />
              <ComposerApply
                config={build.data.config}
                bots={bots.data?.data ?? []}
                engines={engines.data?.data ?? []}
              />
            </div>
          ) : null}
        </Card>
      ) : (
        <Card
          title="Fix an existing bot"
          description="Reads recent calls where somebody spoke and the call was not transferred — the expensive ones, which are not errors anywhere."
        >
          {/* One bot, or the engine all of them were built from. The engine
              is usually the right answer: the same mistake is rarely in one
              script, and fixing the bot fixes one twentieth of it. */}
          <div className="grid gap-3 sm:grid-cols-2">
            <label className="block text-xs text-ink-600 dark:text-ink-300">
              Read the calls of
              <Select
                value={scope}
                onChange={(e) => {
                  setScope(e.target.value as 'bot' | 'engine');
                  setBotId('');
                  setEngineId('');
                }}
                className="mt-1"
              >
                <option value="bot">One bot</option>
                <option value="engine">Every bot on an engine</option>
              </Select>
            </label>

            {scope === 'engine' ? (
              <label className="block text-xs text-ink-600 dark:text-ink-300">
                Bot engine
                <Select
                  value={engineId}
                  onChange={(e) => setEngineId(e.target.value)}
                  className="mt-1"
                >
                  <option value="">Choose an engine…</option>
                  {(engines.data?.data ?? []).map((engine) => (
                    <option key={engine.id} value={engine.id}>
                      {engine.name} — {engine.bots} bot
                      {engine.bots === 1 ? '' : 's'}, {engine.readable_calls}{' '}
                      calls to read
                    </option>
                  ))}
                </Select>
              </label>
            ) : (
              <label className="block text-xs text-ink-600 dark:text-ink-300">
                Bot
                <Select
                  value={botId}
                  onChange={(e) => setBotId(e.target.value)}
                  className="mt-1"
                >
                  <option value="">Choose a bot…</option>
                  {(bots.data?.data ?? []).map((bot) => (
                    <option key={bot.id} value={bot.id}>
                      {bot.client_name} — {bot.name} ({bot.readable_calls} calls
                      to read)
                    </option>
                  ))}
                </Select>
              </label>
            )}
            {/* A list, not a number box. The box let somebody type 60, the
                API refused anything over its ceiling, and what reached the
                operator was a bare "error" with no mention of a limit. */}
            <label className="block text-xs text-ink-600 dark:text-ink-300">
              Calls to read
              <Select
                value={String(calls)}
                onChange={(e) => setCalls(Number(e.target.value))}
                className="mt-1"
              >
                <option value="12">12 — a quick look</option>
                <option value="25">25</option>
                <option value="50">50</option>
                <option value="100">100 — the whole pattern</option>
              </Select>
              <span className="mt-1 block text-2xs text-ink-400">
                {scope === 'engine'
                  ? 'Spread evenly across every bot on the engine, so one busy account cannot fill the sample.'
                  : 'The most recent calls where somebody actually spoke.'}
              </span>
            </label>
          </div>

          {diagnose.error ? (
            <div className="mt-3">
              <ErrorState
                error={diagnose.error}
                retry={() => diagnose.mutate()}
              />
            </div>
          ) : null}

          <Button
            className="mt-3"
            onClick={() => diagnose.mutate()}
            disabled={diagnose.isPending || (scope === 'engine' ? !engineId : !botId)}
          >
            {diagnose.isPending ? 'Reading the calls…' : 'Read the calls'}
          </Button>

          {diagnose.data ? (
            <div className="mt-4 border-t border-line pt-3">
              <p className="text-xs text-ink-500">
                {diagnose.data.calls_read} call
                {diagnose.data.calls_read === 1 ? '' : 's'} read
                {diagnose.data.bot ? ` · ${diagnose.data.bot.name}` : ''}
              </p>

              {diagnose.data.findings.map((finding, index) => (
                <div
                  key={`${finding.problem}-${index}`}
                  className="mt-3 rounded-md border border-line p-3"
                >
                  <div className="flex items-start gap-2">
                    <Badge tone={SEVERITY[finding.severity ?? ''] ?? 'gray'}>
                      {finding.severity ?? 'note'}
                    </Badge>
                    <span className="text-sm font-medium text-ink-800 dark:text-ink-100">
                      {finding.problem}
                    </span>
                  </div>
                  {/* The quote is the whole point. A finding with no evidence
                      is a guess about somebody's live dialling. */}
                  {finding.evidence ? (
                    <blockquote className="mt-2 border-l-2 border-ink-300 pl-3 text-xs italic text-ink-600 dark:border-ink-700 dark:text-ink-300">
                      {finding.evidence}
                    </blockquote>
                  ) : null}
                  {finding.change ? (
                    <p className="mt-2 text-xs text-ink-700 dark:text-ink-200">
                      <strong>Change:</strong> {finding.change}
                      {finding.field ? (
                        <code className="ml-1 text-2xs text-ink-500">
                          {finding.field}
                        </code>
                      ) : null}
                    </p>
                  ) : null}
                </div>
              ))}

              {diagnose.data.reminder ? (
                <p className="mt-3 text-xs text-ink-600 dark:text-ink-300">
                  {diagnose.data.reminder}
                </p>
              ) : null}
              <Notes notes={diagnose.data.notes} />
              <Proposal config={diagnose.data.proposal} />
              {/* Pre-pointed at whatever was just diagnosed, because that is
                  almost always where the fix belongs. */}
              <ComposerApply
                config={diagnose.data.proposal}
                bots={bots.data?.data ?? []}
                engines={engines.data?.data ?? []}
                defaultTarget={scope}
                defaultTargetId={scope === 'engine' ? engineId : botId}
              />
            </div>
          ) : null}
        </Card>
      )}
      <ComposerHistory />
    </div>
  );
}
